(ns measure.reporting-ocp.db.core
  (:require [common.form.core :as form]
            [common.util :as util]
            [measure.messages :refer [ui-messages]]
            [measure.report-builder.core :refer [->adset-ids entity-names]]
            [measure.reporting-ocp.db.options :refer [fields-report-types
                                                      metric-report-types
                                                      multi-adset-report-types
                                                      product-fields-report-types
                                                      use-selected-adset-workflows]]
            [measure.reporting-ocp.db.validation :refer [validate-form]]
            [re-frame.core :as rf]
            [uix.core :refer [defhook]]))

(def initial-value
  {:name ""
   :adset-channel ""
   :adset-ids []
   :date-phrase "last_full_week"
   :date-range [(util/today) (util/today)]
   :timezone util/user-timezone
   :file-format "xlsx"
   :type "adset_summary"
   :schedule nil
   :content-fields []
   :context-fields []
   :product-fields []
   :event-types ["impression"]
   :aggregate-daily false
   :send-emails? false
   :email []})

(form/register-form {:namespace :form.report
                     :validate-form validate-form
                     :initial-value initial-value
                     :submit-event :form.report/submit-form})

(def use-error-message (partial form/use-error-message :form.report))

(def use-bind-control (partial form/use-bind-control :form.report))

(defn ->form [{:keys [date-aggregation email] :as report}]
  (let [form-keys (conj (keys initial-value) :id)
        report* (select-keys report form-keys)]
    (some-> report*
            (assoc :aggregate-daily (= date-aggregation "daily")
                   :adset-ids (->adset-ids report)
                   :send-emails? (boolean (seq email))))))

;; events

(rf/reg-event-fx :form.report/open
  (fn [{:keys [db]} [_ {:keys [entity initial-value]}]]
    (let [initial-value* (some-> initial-value ->form)]
      {:db (assoc-in db [:form.report :meta] {:entity entity :mode (if initial-value* :edit :create)})
       :fx [[:dispatch [:form.report/init initial-value*]]
            [:dispatch [:ocp/open {:id :report/crud
                                   :on-close [:form.report/destroy]}]]]})))

(rf/reg-event-fx :form.report/on-change-adset-channel
  (fn [_ [_ path adset-channel]]
    {:fx [[:dispatch [:form.report/on-change path adset-channel]]
          [:dispatch [:form.report/on-change :adset-ids []]]]}))

(rf/reg-event-fx :form.report/on-change-send-emails?
  (fn [_ [_ path send-emails?]]
    {:fx [[:dispatch [:form.report/on-change path send-emails?]]
          [:dispatch [:form.report/on-change :email []]]]}))

(defn- queue-report-flow
  [{:keys [params type]}]
  {:id :form.report/queue-report-flow
   :first-dispatch [:resource.report/queue-report {:type (keyword type)
                                                   :params params}]
   :rules [{:when :seen?
            :events :resource.report/queue-report-failure
            :halt? true
            :dispatch-fn (fn [[event response]]
                           [[:form.report/submit-form-failure event response]])}
           {:when :seen?
            :events :resource.report/queue-report-success
            :halt? true
            :dispatch-fn (fn [[event response]]
                           [[:form.report/submit-form-success event response]])}]})

(defn- report-schedule-flow
  [{:keys [params mode]}]
  (let [events {:create {:start :resource.report-schedule/post
                         :on-success :resource.report-schedule/post-success
                         :on-failure :resource.report-schedule/post-failure}
                :edit {:start :resource.report-schedule/patch
                       :on-success :resource.report-schedule/patch-success
                       :on-failure :resource.report-schedule/patch-failure}}]
    {:id :form.report/create-report-schedule-flow
     :first-dispatch [(get-in events [mode :start]) params]
     :rules [{:when :seen?
              :events (get-in events [mode :on-failure])
              :halt? true
              :dispatch-fn (fn [[event response]]
                             [[:form.report/submit-form-failure event response]])}
             {:when :seen?
              :events (get-in events [mode :on-success])
              :halt? true
              :dispatch-fn (fn [[event response]]
                             [[:form.report/submit-form-success event response]])}]}))

(rf/reg-event-fx :form.report/submit-form
  [validate-form]
  (fn [{:keys [db]} [_ {:keys [date-range type aggregate-daily adset-ids email] :as value} next-tab]]
    (let [{:keys [entity mode]} (get-in db [:form.report :meta])
          date-range* (->> date-range
                           (mapv #(util/format-iso-string % "yyyy-MM-DD"))
                           (zipmap [:start-date :end-date]))
          params (cond-> (dissoc value :adset-channel :date-range :aggregate-daily :send-emails?)
                   aggregate-daily (assoc :date-aggregation "daily")
                   (= entity :report) (-> (merge date-range*)
                                          (dissoc :date-phrase :type))
                   (seq email) (update :email (partial mapv #(get % :value %)))
                   (not (contains? metric-report-types type)) (dissoc :event-types)
                   (not (contains? fields-report-types type)) (dissoc :content-fields :context-fields)
                   (not (contains? product-fields-report-types type)) (dissoc :product-fields)
                   ;; temporary fix, as only custom reports allow multiple adsets for now,
                   ;; can be removed once we've added multi adset reporting support for other report types.
                   (not (multi-adset-report-types type)) (-> (dissoc :adset-ids)
                                                             (assoc :adset-id (first adset-ids)))
                   true util/filter-nils-map)
          next-tab (next-tab)]
      (when (nil? next-tab)
        {:fx [[:dispatch [:loader/open]]
              [:async-flow (if (= entity :report)
                             (queue-report-flow {:params params :type type})
                             (report-schedule-flow {:params params :mode mode}))]]}))))

(rf/reg-event-fx :form.report/submit-form-success
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [db url-context]} _]
    (let [{:keys [entity mode]} (get-in db [:form.report :meta])
          report-type (get-in db [:form.report :value :type])
          message-path (if (= entity :report)
                         [entity mode (keyword report-type) :success]
                         [entity mode :success])]
      {:fx [[:dispatch [:loader/close]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name (get entity-names entity)
                         :type :success
                         :message (get-in ui-messages message-path)}]]
            [:dispatch [:ocp/close]]
            (if (= entity :report)
              [:invalidate-queries {:queryKey ["reports" (:advertiser-id url-context)]}]
              [:invalidate-queries {:queryKey ["report-schedules" (:advertiser-id url-context)]}])]})))

(rf/reg-event-fx :form.report/submit-form-failure
  (fn [{:keys [db]} [_ event response]]
    (let [{:keys [entity mode]} (get-in db [:form.report :meta])
          report-type (get-in db [:form.report :value :type])
          message-path (if (= entity :report)
                         [entity mode (keyword report-type) :failure]
                         [entity mode :failure])]
      {:fx [[:dispatch [:loader/close]]
            [:dispatch [:response-handler/handle-response
                        {:entity-name (get entity-names entity)
                         :type :error
                         :event event
                         :message (get-in ui-messages message-path)
                         :response response}]]]})))

(rf/reg-sub :form.report/error-in-fields?
  (fn [[_ paths]]
    (mapv (fn [path] (rf/subscribe [:form.report/error-message path])) paths))
  (fn [error-messages _]
    (some some? error-messages)))

(defhook use-valid-adset-workflows []
  (let [selected-adset-workflows (use-selected-adset-workflows)]
    (or (= #{"ad-variants"} selected-adset-workflows)
        (not (contains? selected-adset-workflows "ad-variants")))))
